import { ChangeDetectionStrategy, Component, computed, inject, resource, signal, WritableSignal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { DbTemplateService } from '../db-template.service';
import {
  DbObjectItemCreatedByEnum, DbTemplateSchemaChange, DbTemplateSchemaColumn, DbTemplateSchemaDecision,
  SchemaChangeAction, SchemaChangeType
} from '../db-template.model';

/** Valget i en rækkes dropdown: 'apply' | 'hidden' | 'ignore' | 'rename:<kolonne>'. */
type Choice = string;

interface ChangeRow {
  change: DbTemplateSchemaChange;
  key: string;
  choice: WritableSignal<Choice>;
  /** Kun for ændrede felter, hvor kolonnen er blevet NOT NULL og feltet ikke allerede er obligatorisk. */
  offerMandatory: boolean;
  makeMandatory: WritableSignal<boolean>;
}

const RENAME = 'rename:';

/**
 * "Tjek databasetabel": viser forskellene mellem databaseskabelonen og tabellen/viewet/stored proceduren, som de
 * er nu, og lader admin vælge pr. forskel, før noget ændres. Et fjernet felt kan peges på en ny kolonne
 * (omdøbning), så felt-id'et — og dermed afdelingernes skabelonfelter og filtrene — bevares.
 */
@Component({
  selector: 'app-db-template-schema-diff',
  templateUrl: './db-template-schema-diff.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DbTemplateSchemaDiffComponent {
  readonly activeModal = inject(NgbActiveModal);
  private readonly service = inject(DbTemplateService);
  private readonly toastr = inject(ToastrService);

  // Sættes af den, der åbner modalen (NgbModal sætter ikke signal-inputs); indlæsningen venter på id'et.
  readonly dbTemplateId = signal('');
  readonly dbTemplateName = signal('');

  readonly diff = resource({
    params: () => this.dbTemplateId() || undefined,
    loader: ({ params }) => this.service.getDbTemplateSchemaDiff(params)
  });

  readonly applying = signal(false);
  readonly changeType = SchemaChangeType;

  readonly rows = computed<ChangeRow[]>(() => (this.diff.hasValue() ? this.diff.value().changes : []).map(change => {
    const becameNotNull = change.changeType == SchemaChangeType.Modified
      && !!change.template?.isNullable && change.database?.isNullable === false;
    return {
      change,
      key: change.dbTemplateItemId ?? ('added:' + change.origin + ':' + change.columnName),
      choice: signal<Choice>(change.changeType == SchemaChangeType.Removed && change.usedInFilters > 0 ? 'ignore' : 'apply'),
      offerMandatory: becameNotNull && !change.mandatory,
      makeMandatory: signal(becameNotNull && !change.mandatory)
    };
  }));

  readonly added = computed(() => this.rows().filter(x => x.change.changeType == SchemaChangeType.Added));
  readonly removed = computed(() => this.rows().filter(x => x.change.changeType == SchemaChangeType.Removed));
  readonly modified = computed(() => this.rows().filter(x => x.change.changeType == SchemaChangeType.Modified));

  /** Nye kolonner, som et fjernet felt er valgt omdøbt til — de tilføjes ikke også som nye felter. */
  readonly renameTargets = computed(() => new Set(this.removed()
    .map(x => x.choice())
    .filter(x => x.startsWith(RENAME))
    .map(x => x.substring(RENAME.length).toLowerCase())));

  readonly summary = computed(() => {
    const targets = this.renameTargets();
    const added = this.added().filter(x => x.choice() != 'ignore' && !targets.has(x.change.columnName.toLowerCase()));
    const removed = this.removed().filter(x => x.choice() == 'apply');
    return {
      added: added.length,
      removed: removed.length,
      renamed: this.removed().filter(x => x.choice().startsWith(RENAME)).length,
      modified: this.modified().filter(x => x.choice() == 'apply').length,
      // Felter der forsvinder fra afdelingernes skabeloner — det skal admin se, før der trykkes.
      removedFromTemplates: removed.reduce((sum, x) => sum + x.change.usedInTemplates, 0)
    };
  });

  readonly nothingChosen = computed(() => {
    const s = this.summary();
    return s.added + s.removed + s.renamed + s.modified == 0;
  });

  /** Nye kolonner, et fjernet felt kan være omdøbt til (dem, et andet fjernet felt ikke allerede har valgt). */
  renameOptions(row: ChangeRow): DbTemplateSchemaChange[] {
    const takenByOthers = new Set(this.removed()
      .filter(x => x !== row && x.choice().startsWith(RENAME))
      .map(x => x.choice().substring(RENAME.length).toLowerCase()));
    return this.added().map(x => x.change).filter(x => !takenByOthers.has(x.columnName.toLowerCase()));
  }

  isRenameTarget(row: ChangeRow): boolean {
    return this.renameTargets().has(row.change.columnName.toLowerCase());
  }

  setChoice(row: ChangeRow, event: Event): void {
    row.choice.set((event.target as HTMLSelectElement).value);
  }

  setMakeMandatory(row: ChangeRow, event: Event): void {
    row.makeMandatory.set((event.target as HTMLInputElement).checked);
  }

  describe(column: DbTemplateSchemaColumn | null): string {
    if (column == null) {
      return '';
    }
    let text = column.dataType;
    if (column.maxLength != null) {
      text += '(' + (column.maxLength == -1 ? 'max' : column.maxLength) + ')';
    }
    text += column.isNullable ? ' NULL' : ' NOT NULL';
    if (column.defaultValue) {
      text += ' default ' + column.defaultValue;
    }
    return text;
  }

  originName(origin: DbObjectItemCreatedByEnum): string {
    switch (origin) {
      case DbObjectItemCreatedByEnum.SPInput: return 'SP Input';
      case DbObjectItemCreatedByEnum.SPOutput: return 'SP Output';
      default: return '';
    }
  }

  async apply(): Promise<void> {
    const decisions: DbTemplateSchemaDecision[] = this.rows().map(row => {
      const choice = row.choice();
      const isRename = choice.startsWith(RENAME);
      return {
        changeType: row.change.changeType,
        dbTemplateItemId: row.change.dbTemplateItemId,
        columnName: row.change.columnName,
        origin: row.change.origin,
        action: isRename ? SchemaChangeAction.RenameTo
          : choice == 'apply' ? SchemaChangeAction.Apply
          : choice == 'hidden' ? SchemaChangeAction.AddHidden
          : SchemaChangeAction.Ignore,
        renameToColumnName: isRename ? choice.substring(RENAME.length) : null,
        makeMandatory: row.offerMandatory && row.makeMandatory()
      };
    });

    this.applying.set(true);
    try {
      const result = await this.service.applyDbTemplateSchemaDiff(this.dbTemplateId(), decisions);
      this.toastr.success(`${result.added} tilføjet, ${result.removed} fjernet, ${result.modified} ændret, ${result.renamed} omdøbt`, 'Databaseskabelonen er opdateret');
      this.activeModal.close(result);
    } catch {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    } finally {
      this.applying.set(false);
    }
  }
}
